/**
 * Search Pane — reusable cross-chapter search UI with integrated tag filtering.
 */

import { Clear, DoneAll, LocalOffer, Preview } from '@mui/icons-material'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { SearchResult } from '../../services/api'
import {
  TagDefinition,
  TagTreeNode,
  buildTree,
  collectHiddenIds,
  getNodeCount,
  resolveTagColor,
} from '../tags/tagTreeUtils'

export interface SearchPaneHandle {
  focusInput: () => void
}

const SearchHeadingPathChips: React.FC<{ headingPath: string[] }> = ({ headingPath }) => {
  const scrollerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!scrollerRef.current) {
      return
    }
    scrollerRef.current.scrollLeft = scrollerRef.current.scrollWidth
  }, [headingPath])

  if (headingPath.length === 0) {
    return null
  }

  return (
    <Box
      ref={scrollerRef}
      sx={{
        mb: 0.4,
        overflowX: 'auto',
        overflowY: 'hidden',
        direction: 'ltr',
        whiteSpace: 'nowrap',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <Box sx={{ display: 'inline-flex', gap: 0.5, minWidth: 'max-content', pr: 0.25 }}>
        {headingPath.map((heading, index) => (
          <Chip
            key={`${heading}-${index}`}
            label={heading}
            size="small"
            variant="outlined"
            sx={{
              height: 18,
              '& .MuiChip-label': {
                px: 0.8,
                fontSize: '0.66rem',
                lineHeight: 1.2,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Tag filter popover — compact multi-select tag tree
// ---------------------------------------------------------------------------

interface TagFilterPopoverProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  tags: Record<string, TagDefinition>
  tagColorMap: Record<string, string>
  selectedTagIds: string[]
  usageCounts: Record<string, number>
  onToggleTag: (tagId: string) => void
  direction?: 'ltr' | 'rtl'
}

const TagFilterPopover: React.FC<TagFilterPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  tags,
  tagColorMap,
  selectedTagIds,
  usageCounts,
  onToggleTag,
  direction = 'ltr',
}) => {
  const [filterText, setFilterText] = React.useState('')
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
  const [multiSelect, setMultiSelect] = React.useState(false)
  const filterInputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  // Reset state when popover closes
  React.useEffect(() => {
    if (!open) {
      setFilterText('')
      setHighlightedIndex(-1)
      setMultiSelect(false)
    }
  }, [open])

  // In single-select mode, close the popover after each toggle
  const handleToggle = (id: string) => {
    onToggleTag(id)
    if (!multiSelect) {
      onClose()
    }
  }

  // Auto-focus when popover opens
  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(() => filterInputRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  const visibleTags = React.useMemo(() => {
    const hiddenIds = collectHiddenIds(tags)
    return Object.fromEntries(Object.entries(tags).filter(([id]) => !hiddenIds.has(id)))
  }, [tags])

  const tree = React.useMemo(() => buildTree(visibleTags), [visibleTags])

  // Flat filtered list — used when the user has typed something
  const filteredEntries = React.useMemo(() => {
    const q = filterText.trim().toLowerCase()
    if (!q) {
      return null
    }
    return Object.entries(visibleTags)
      .filter(([, def]) => def.name.toLowerCase().includes(q))
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
  }, [filterText, visibleTags])

  // Pre-computed set of tag IDs that are parents (have children) — used to distinguish leaf tags
  const parentIdSet = React.useMemo(() => {
    const s = new Set<string>()
    for (const def of Object.values(visibleTags)) {
      if (def.parentIds) {
        for (const pid of def.parentIds) {
          s.add(pid)
        }
      }
    }
    return s
  }, [visibleTags])

  // Ordered list of navigable (selectable) tag IDs for keyboard nav
  const navigableIds = React.useMemo(() => {
    if (filteredEntries !== null) {
      return filteredEntries.map(([id]) => id)
    }
    const result: string[] = []
    const flatten = (nodes: TagTreeNode[]) => {
      for (const node of nodes) {
        if (node.isTag) {
          result.push(node.id)
        }
        flatten(node.children)
      }
    }
    flatten(tree)
    return result
  }, [filteredEntries, tree])

  // Reset highlight when filter text or navigable list changes
  React.useEffect(() => {
    setHighlightedIndex(-1)
  }, [filterText, navigableIds.length])

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) {
      return
    }
    const id = navigableIds[highlightedIndex]
    if (!id) {
      return
    }
    const el = listRef.current.querySelector<HTMLElement>(`[data-tag-nav-id="${CSS.escape(id)}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, navigableIds])

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, navigableIds.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0) {
        e.preventDefault()
        const id = navigableIds[highlightedIndex]
        if (id) {
          handleToggle(id)
        }
      }
    } else if (e.key === 'Escape') {
      if (filterText) {
        setFilterText('')
      } else {
        onClose()
      }
    }
  }

  const renderRow = (id: string, depth: number, isLeaf: boolean) => {
    const isSelected = selectedTagIds.includes(id)
    const isHighlighted = navigableIds[highlightedIndex] === id
    const nodeColor = resolveTagColor(id, tagColorMap, tags)
    const count = usageCounts[id] ?? 0
    const def = tags[id]
    if (!def) {
      return null
    }

    return (
      <Box
        key={id}
        data-tag-nav-id={id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          paddingInlineStart: `${depth * 16 + 6}px`,
          paddingInlineEnd: '6px',
          py: 0.3,
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: isHighlighted ? 'action.focus' : isSelected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: isHighlighted ? 'action.focus' : isSelected ? 'action.selected' : 'action.hover' },
        }}
        onClick={() => handleToggle(id)}
      >
        {nodeColor ? (
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: nodeColor, flexShrink: 0 }} />
        ) : null}
        <Typography
          sx={{
            flex: 1,
            fontSize: '0.78rem',
            color: isSelected ? 'var(--sidebar-selected-text)' : 'var(--text-primary)',
          }}
        >
          {isLeaf && (
            <Box
              component="span"
              sx={{ color: 'text.disabled', marginInlineEnd: 0.4, fontSize: '0.9em', userSelect: 'none' }}
            >
              #
            </Box>
          )}
          {def.name}
        </Typography>
        {count > 0 && (
          <Typography
            component="span"
            sx={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', minWidth: 14, textAlign: 'center' }}
          >
            {count}
          </Typography>
        )}
      </Box>
    )
  }

  const renderNode = (node: TagTreeNode, depth: number): React.ReactNode => {
    const isSelected = selectedTagIds.includes(node.id)
    const isHighlighted = navigableIds[highlightedIndex] === node.id
    const nodeColor = node.isTag ? resolveTagColor(node.id, tagColorMap, tags) : undefined
    const count = getNodeCount(node.id, usageCounts, tags)

    return (
      <Box key={node.id}>
        <Box
          data-tag-nav-id={node.isTag ? node.id : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            paddingInlineStart: `${depth * 16 + 6}px`,
            paddingInlineEnd: '6px',
            py: 0.3,
            cursor: node.isTag ? 'pointer' : 'default',
            borderRadius: 1,
            bgcolor: isHighlighted ? 'action.focus' : isSelected ? 'action.selected' : 'transparent',
            '&:hover': node.isTag
              ? { bgcolor: isHighlighted ? 'action.focus' : isSelected ? 'action.selected' : 'action.hover' }
              : {},
          }}
          onClick={() => node.isTag && handleToggle(node.id)}
        >
          {nodeColor ? (
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: nodeColor, flexShrink: 0 }} />
          ) : null}
          <Typography
            sx={{
              flex: 1,
              fontSize: '0.78rem',
              fontWeight: node.children.length > 0 && !node.isTag ? 600 : 400,
              color: node.isTag
                ? isSelected
                  ? 'var(--sidebar-selected-text)'
                  : 'var(--text-primary)'
                : 'var(--text-secondary)',
            }}
          >
            {!node.children.length && (
              <Box
                component="span"
                sx={{ color: 'text.disabled', marginInlineEnd: 0.4, fontSize: '0.9em', userSelect: 'none' }}
              >
                #
              </Box>
            )}
            {node.name}
          </Typography>
          {count > 0 && (
            <Typography
              component="span"
              sx={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', minWidth: 14, textAlign: 'center' }}
            >
              {count}
            </Typography>
          )}
        </Box>
        {node.children.map((child) => renderNode(child, depth + 1))}
      </Box>
    )
  }

  const popoverSide = direction === 'rtl' ? 'right' : 'left'

  // Bail out before building the tree, not inside <Popover>. A closed Popover
  // renders nothing, but its children are constructed first — so the whole tag
  // tree was being walked (a getNodeCount per node, each one O(tags)) on every
  // render of this pane, popover open or not. With the registry at ~750 tags
  // that is ~600ms of blocked main thread per render, which lands on the
  // author as a stall every time the editor syncs while typing.
  if (!open) {
    return null
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: popoverSide }}
      transformOrigin={{ vertical: 'top', horizontal: popoverSide }}
      disableRestoreFocus
      PaperProps={{
        dir: direction,
        sx: {
          mt: 0.5,
          width: 220,
          maxHeight: 360,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--floating-panel-shadow, 0 4px 20px rgba(0,0,0,0.15))',
        },
      }}
    >
      {/* Filter input */}
      <Box
        sx={{
          px: 1,
          pt: 0.75,
          pb: 0.5,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <TextField
          sx={{ flex: 1 }}
          inputRef={filterInputRef}
          size="small"
          fullWidth
          placeholder="Find tag…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          onKeyDown={handleInputKeyDown}
          autoComplete="off"
          inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: false }}
          InputProps={{
            endAdornment: filterText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setFilterText('')}
                  edge="end"
                  sx={{ color: 'var(--icon-default)', p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              fontSize: '0.8rem',
              '& input': { color: 'var(--text-primary)', py: '5px' },
              '& input::placeholder': { color: 'var(--text-secondary)', opacity: 1 },
            },
          }}
        />
        <Tooltip
          title={
            multiSelect ? 'Multi-select on — click to switch to single' : 'Single select — click to keep open for multi'
          }
          placement="top"
          disableInteractive
        >
          <IconButton
            size="small"
            onClick={() => setMultiSelect((m) => !m)}
            sx={{ color: multiSelect ? 'primary.main' : 'var(--icon-default)', flexShrink: 0, p: 0.375 }}
          >
            <DoneAll sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tag list */}
      <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {filteredEntries !== null ? (
          filteredEntries.length === 0 ? (
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-secondary)', px: 1.5, py: 1 }}>
              No matching tags
            </Typography>
          ) : (
            filteredEntries.map(([id]) => renderRow(id, 0, !parentIdSet.has(id)))
          )
        ) : tree.length === 0 ? (
          <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-secondary)', px: 1.5, py: 1 }}>
            No tags defined
          </Typography>
        ) : (
          tree.map((node) => renderNode(node, 0))
        )}
      </Box>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// Search input — owns its own local state so keystrokes don't re-render the
// rest of SearchPane (tag row, results list, tag popover). The query is
// committed only on Enter: no timer fires mid-typing, so a keystroke can never
// land while the results are re-rendering. Focus stays in the field after the
// search, so the author can keep narrowing a query that returned too much.
// ---------------------------------------------------------------------------

interface SearchInputFieldProps {
  committedQuery: string
  /** Bumped by the pane whenever it clears the search, so the field empties too. */
  clearSignal: number
  loading: boolean
  inputRef: React.RefObject<HTMLInputElement>
  autoFocus: boolean
  textAlign: 'left' | 'right'
  onCommit: (query: string) => void
  onClear: () => void
  onPanelKey: (key: string) => boolean
}

const SearchInputField: React.FC<SearchInputFieldProps> = React.memo(
  ({ committedQuery, clearSignal, loading, inputRef, autoFocus, textAlign, onCommit, onClear, onPanelKey }) => {
    const [localQuery, setLocalQuery] = useState(committedQuery)

    useEffect(() => {
      setLocalQuery(committedQuery)
    }, [committedQuery])

    const lastClearSignalRef = useRef(clearSignal)
    useEffect(() => {
      if (lastClearSignalRef.current === clearSignal) {
        return
      }
      lastClearSignalRef.current = clearSignal
      setLocalQuery('')
    }, [clearSignal])

    const isPending = localQuery !== committedQuery

    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        autoComplete="off"
        placeholder="Search"
        value={localQuery}
        onChange={(e) => setLocalQuery(e.target.value)}
        onKeyDown={(e) => {
          // Enter commits the typed query; only once it is committed does Enter
          // fall through to the panel (open the selected result).
          if (e.key === 'Enter' && isPending) {
            onCommit(localQuery)
            e.preventDefault()
            return
          }
          if (onPanelKey(e.key)) {
            e.preventDefault()
          }
        }}
        autoFocus={autoFocus}
        inputProps={{
          autoComplete: 'off',
          autoCorrect: 'off',
          autoCapitalize: 'none',
          spellCheck: false,
          name: 'search-input',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <>
                  {isPending ? (
                    <Typography
                      component="span"
                      sx={{ fontSize: '0.7rem', color: 'var(--text-secondary)', mx: 0.5, whiteSpace: 'nowrap' }}
                    >
                      ⏎
                    </Typography>
                  ) : null}
                  {localQuery ? (
                    <IconButton size="small" onClick={onClear} edge="end" sx={{ color: 'var(--icon-default)' }}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : null}
                </>
              )}
            </InputAdornment>
          ),
          sx: {
            fontSize: '0.85rem',
            '& input': {
              color: 'var(--text-primary)',
              textAlign,
            },
            '& input::placeholder': {
              color: 'var(--text-secondary)',
              opacity: 1,
            },
          },
        }}
      />
    )
  },
)
SearchInputField.displayName = 'SearchInputField'

// ---------------------------------------------------------------------------
// Main SearchPane
// ---------------------------------------------------------------------------

interface SearchPaneProps {
  query: string
  results: SearchResult[]
  loading: boolean
  searched: boolean
  direction?: 'ltr' | 'rtl'
  onQueryChange: (query: string) => void
  onClear: () => void
  /** Clicking a result row → go to the match in the editor. */
  onNavigate: (filename: string, blockId: string, matchText: string, matchStart: number) => void
  /** Clicking a row's preview icon → peek at the match without leaving the current chapter. */
  onPreviewSelect?: (filename: string, blockId: string, matchText: string, matchStart: number) => void
  onStepSelection?: (direction: -1 | 1) => void
  onEnterSelection?: () => void
  clearOnEscape?: boolean
  autoFocus?: boolean
  selectedResultKey?: string | null
  resultsBottomInset?: number
  isPreviewOpen?: boolean
  onClosePreview?: () => void
  // Tag filter props
  selectedTagIds?: string[]
  onTagsChange?: (tagIds: string[]) => void
  tags?: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  tagUsageCounts?: Record<string, number>
  onOpenTagManager?: () => void
}

const SearchPane = forwardRef<SearchPaneHandle, SearchPaneProps>(
  (
    {
      query,
      results,
      loading,
      searched,
      direction = 'ltr',
      onQueryChange,
      onClear,
      onNavigate,
      onPreviewSelect,
      onStepSelection,
      onEnterSelection,
      clearOnEscape = true,
      autoFocus = false,
      selectedResultKey,
      resultsBottomInset = 0,
      isPreviewOpen = false,
      onClosePreview,
      selectedTagIds = [],
      onTagsChange,
      tags = {},
      tagColorMap = {},
      tagUsageCounts = {},
      onOpenTagManager,
    },
    ref,
  ) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const resultsContainerRef = useRef<HTMLDivElement>(null)
    const panelKeyboardActiveRef = useRef(false)
    const [tagPopoverAnchor, setTagPopoverAnchor] = useState<HTMLElement | null>(null)

    const hasTagsAvailable = Object.keys(tags).length > 0

    useImperativeHandle(ref, () => ({
      focusInput: () => {
        inputRef.current?.focus()
        inputRef.current?.select()
      },
    }))

    useEffect(() => {
      if (autoFocus) {
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }, [autoFocus])

    useEffect(() => {
      if (!selectedResultKey || !resultsContainerRef.current) {
        return
      }

      const selectedElement = resultsContainerRef.current.querySelector<HTMLElement>(
        `[data-search-result-key="${CSS.escape(selectedResultKey)}"]`,
      )
      selectedElement?.scrollIntoView({ block: 'nearest' })
    }, [selectedResultKey])

    const [clearSignal, setClearSignal] = useState(0)

    const handleClear = useCallback(() => {
      setClearSignal((n) => n + 1)
      onClear()
      inputRef.current?.focus()
    }, [onClear])

    const resultsLengthRef = useRef(results.length)
    useEffect(() => {
      resultsLengthRef.current = results.length
    }, [results.length])

    const handleSearchPanelKey = useCallback(
      (key: string): boolean => {
        if (key === 'Escape') {
          if (isPreviewOpen && onClosePreview) {
            onClosePreview()
            return true
          }
          if (clearOnEscape) {
            handleClear()
            return true
          }
        }

        if ((key === 'ArrowDown' || key === 'ArrowUp') && resultsLengthRef.current > 0 && onStepSelection) {
          onStepSelection(key === 'ArrowDown' ? 1 : -1)
          return true
        }

        if (key === 'Enter' && resultsLengthRef.current > 0 && onEnterSelection) {
          onEnterSelection()
          return true
        }

        return false
      },
      [isPreviewOpen, onClosePreview, clearOnEscape, handleClear, onStepSelection, onEnterSelection],
    )

    useEffect(() => {
      const handleDocumentPointerDown = (event: PointerEvent) => {
        const target = event.target
        panelKeyboardActiveRef.current = !!(target instanceof Node && rootRef.current?.contains(target))
      }

      const handleDocumentFocusIn = (event: FocusEvent) => {
        const target = event.target
        panelKeyboardActiveRef.current = !!(target instanceof Node && rootRef.current?.contains(target))
      }

      const handleDocumentKeyDown = (event: KeyboardEvent) => {
        if (!panelKeyboardActiveRef.current) {
          return
        }

        if (event.target === inputRef.current) {
          return
        }

        const target = event.target
        if (
          target instanceof HTMLElement &&
          !rootRef.current?.contains(target) &&
          (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
        ) {
          return
        }

        if (handleSearchPanelKey(event.key)) {
          event.preventDefault()
        }
      }

      document.addEventListener('pointerdown', handleDocumentPointerDown)
      document.addEventListener('focusin', handleDocumentFocusIn)
      document.addEventListener('keydown', handleDocumentKeyDown)

      return () => {
        document.removeEventListener('pointerdown', handleDocumentPointerDown)
        document.removeEventListener('focusin', handleDocumentFocusIn)
        document.removeEventListener('keydown', handleDocumentKeyDown)
      }
    }, [handleSearchPanelKey])

    const handleToggleTag = (tagId: string) => {
      if (!onTagsChange) {
        return
      }
      if (selectedTagIds.includes(tagId)) {
        onTagsChange(selectedTagIds.filter((id) => id !== tagId))
      } else {
        onTagsChange([...selectedTagIds, tagId])
      }
    }

    const handleOpenTagSelector = (anchor: HTMLElement) => {
      setTagPopoverAnchor(anchor)
    }

    const resultsAlign = direction === 'rtl' ? 'right' : 'left'
    const resultsCountLabel =
      results.length === 1 ? '1 result found' : `${results.length.toLocaleString()} results found`

    return (
      <Box
        ref={rootRef}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        dir={direction}
      >
        {/* Search input */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: hasTagsAvailable ? 'none' : '1px solid var(--border-subtle)' }}>
          <SearchInputField
            committedQuery={query}
            clearSignal={clearSignal}
            loading={loading}
            inputRef={inputRef}
            autoFocus={autoFocus}
            textAlign={resultsAlign}
            onCommit={onQueryChange}
            onClear={handleClear}
            onPanelKey={handleSearchPanelKey}
          />
        </Box>

        {/* Tag filter row — shown when tags are available */}
        {hasTagsAvailable && (
          <Box
            onClick={(e) => handleOpenTagSelector(e.currentTarget)}
            sx={{
              px: 1.5,
              py: 0.6,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              borderBottom: '1px solid var(--border-subtle)',
              minHeight: 34,
              flexWrap: 'nowrap',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            {/* Selected tag chips */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                flex: 1,
                overflow: 'hidden',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              {selectedTagIds.length === 0 ? (
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                  Filter by tag
                </Typography>
              ) : (
                selectedTagIds.map((tagId) => {
                  const tagDef = tags[tagId]
                  if (!tagDef) {
                    return null
                  }
                  const color = tagColorMap[tagId]
                  return (
                    <Box
                      key={tagId}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 22,
                        bgcolor: color ? `${color}22` : 'action.selected',
                        border: color ? `1px solid ${color}44` : '1px solid var(--border-subtle)',
                        borderRadius: '11px',
                        flexShrink: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Color dot — at inline-start */}
                      {color && (
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0,
                            marginInlineStart: '6px',
                          }}
                        />
                      )}
                      {/* Label */}
                      <Typography sx={{ fontSize: '0.7rem', color: 'var(--text-primary)', lineHeight: 1, px: '7px' }}>
                        {tagDef.name}
                      </Typography>
                      {/* Delete button — at inline-end */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleTag(tagId)
                        }}
                        sx={{
                          p: 0,
                          height: 22,
                          width: 20,
                          flexShrink: 0,
                          color: 'var(--text-secondary)',
                          borderRadius: 0,
                          bgcolor: 'rgba(128,128,128,0.18)',
                          '&:hover': { color: 'var(--text-primary)', bgcolor: 'rgba(128,128,128,0.32)' },
                        }}
                      >
                        <Clear sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Box>
                  )
                })
              )}
            </Box>

            {onOpenTagManager && (
              <Tooltip title="Manage tags" placement="top" disableInteractive>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenTagManager()
                  }}
                  sx={{
                    color: 'var(--icon-default)',
                    flexShrink: 0,
                    p: 0.5,
                  }}
                >
                  <LocalOffer sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Results */}
        <Box
          ref={resultsContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2.5,
            pt: 1,
            pb: `calc(8px + ${Math.max(0, Math.round(resultsBottomInset))}px)`,
            direction: direction === 'rtl' ? 'ltr' : undefined,
          }}
        >
          {searched && !loading && results.length > 0 && (
            <Typography
              sx={{
                color: 'var(--text-tertiary)',
                fontSize: '0.72rem',
                textAlign: resultsAlign,
                mb: 0.75,
              }}
            >
              {resultsCountLabel}
            </Typography>
          )}

          {!searched && !loading && (
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'center', mt: 2 }}>
              {hasTagsAvailable ? 'Search or select tags to filter' : 'Type to search across all chapters'}
            </Typography>
          )}

          {searched && results.length === 0 && (
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'center', mt: 2 }}>
              No results
            </Typography>
          )}

          {results.map((r) => {
            const resultKey = `content:${r.filename}:${r.blockId}:${r.blockMatchStart}:${r.annotationType ?? 'text'}:${(r.tagIds ?? []).slice().sort().join(',')}`
            const isSelected = selectedResultKey === resultKey
            const hasHighlight = r.matchStart < r.matchEnd
            const matchText = hasHighlight ? r.snippet.slice(r.matchStart, r.matchEnd) : ''

            return (
              <Box
                key={resultKey}
                data-search-result-key={resultKey}
                sx={{
                  position: 'relative',
                  px: 1,
                  py: 0.9,
                  cursor: 'pointer',
                  borderRadius: 1,
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  textAlign: resultsAlign,
                  borderBottom: '1px solid var(--search-result-divider)',
                  '&:hover .search-result-preview-button': { opacity: 1 },
                }}
                onClick={() => onNavigate(r.filename, r.blockId, matchText, r.blockMatchStart)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        mb: r.headingPath.length > 0 ? 0 : 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.chapterTitle}
                    </Typography>
                    <SearchHeadingPathChips headingPath={r.headingPath} />
                  </Box>
                  {onPreviewSelect && (
                    <Tooltip title="Preview">
                      <IconButton
                        className="search-result-preview-button"
                        size="small"
                        aria-label="Preview this result"
                        onClick={(e) => {
                          e.stopPropagation()
                          onPreviewSelect(r.filename, r.blockId, matchText, r.blockMatchStart)
                        }}
                        sx={{
                          p: 0.25,
                          flexShrink: 0,
                          color: 'var(--icon-default)',
                          opacity: isSelected ? 1 : 0.4,
                          transition: 'opacity 120ms',
                        }}
                      >
                        <Preview sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-primary)', lineHeight: 1.4 }}>
                  {r.snippet.slice(0, r.matchStart)}
                  {hasHighlight && (
                    <Box
                      component="span"
                      sx={{
                        bgcolor: 'var(--search-highlight)',
                        color: 'var(--search-highlight-text)',
                        fontWeight: 700,
                      }}
                    >
                      {r.snippet.slice(r.matchStart, r.matchEnd)}
                    </Box>
                  )}
                  {r.snippet.slice(hasHighlight ? r.matchEnd : 0)}
                </Typography>
                {/* Tag badges for tag-based results */}
                {r.annotationType === 'heading' && (
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontSize: '0.62rem',
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Heading
                  </Typography>
                )}
                {r.tagIds && r.tagIds.length > 0 && (
                  <Box sx={{ mt: 0.4, display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                    {r.tagIds.map((tagId) => {
                      const tagDef = tags[tagId]
                      if (!tagDef) {
                        return null
                      }
                      const color = tagColorMap[tagId]
                      return (
                        <Box
                          key={tagId}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.3,
                            fontSize: '0.62rem',
                            color: color || 'var(--text-secondary)',
                            border: `1px solid ${color ? color + '66' : 'var(--border-subtle)'}`,
                            borderRadius: 0.5,
                            px: 0.5,
                            py: 0.1,
                            bgcolor: color ? `${color}11` : 'transparent',
                          }}
                        >
                          {color && (
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          )}
                          #{tagDef.name}
                        </Box>
                      )
                    })}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>

        {/* Tag filter popover */}
        <TagFilterPopover
          anchorEl={tagPopoverAnchor}
          open={Boolean(tagPopoverAnchor)}
          onClose={() => {
            setTagPopoverAnchor(null)
            setTimeout(() => inputRef.current?.focus(), 0)
          }}
          tags={tags}
          tagColorMap={tagColorMap}
          selectedTagIds={selectedTagIds}
          usageCounts={tagUsageCounts}
          onToggleTag={handleToggleTag}
          direction={direction}
        />
      </Box>
    )
  },
)

SearchPane.displayName = 'SearchPane'

export default SearchPane
